import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { InfraCostAggregate, LlmCostEntry } from "@/providers/costs.provider";
import { Icon } from "@iconify/react";

interface CostsPanelProps {
  costs?: InfraCostAggregate;
  isLoading?: boolean;
  days: number;
}

function formatTokens(n: number | undefined) {
  return (n ?? 0).toLocaleString();
}

function formatUsd(n: number | undefined) {
  return (n ?? 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatUsdPerCall(n: number) {
  // Per-call costs are often sub-cent — 2 decimals would round most of them
  // to $0.00, so allow up to 6.
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}

/** total_cost_usd: 0 (or missing) is ambiguous — "actually free" vs "couldn't be priced". */
function CostCell({ entry }: { entry: LlmCostEntry }) {
  if (!entry.total_cost_usd && entry.call_count > 0) {
    return (
      <span className="text-muted-foreground" title="Cost unavailable for this model">
        cost unavailable
      </span>
    );
  }
  return <>{formatUsd(entry.total_cost_usd)}</>;
}

function CostPerCallCell({ entry }: { entry: LlmCostEntry }) {
  if (!entry.call_count) {
    return <span className="text-muted-foreground/50">—</span>;
  }
  if (!entry.total_cost_usd) {
    return (
      <span className="text-muted-foreground" title="Cost unavailable for this model">
        cost unavailable
      </span>
    );
  }
  return <>{formatUsdPerCall(entry.total_cost_usd / entry.call_count)}</>;
}

function sumTotals(entries: LlmCostEntry[]) {
  return entries.reduce(
    (acc, entry) => ({
      calls: acc.calls + (entry.call_count ?? 0),
      input: acc.input + (entry.total_input_tokens ?? 0),
      output: acc.output + (entry.total_output_tokens ?? 0),
      thoughts: acc.thoughts + (entry.total_thoughts_tokens ?? 0),
      cost: acc.cost + (entry.total_cost_usd ?? 0),
      hasUnpriced: acc.hasUnpriced || (!entry.total_cost_usd && entry.call_count > 0),
    }),
    { calls: 0, input: 0, output: 0, thoughts: 0, cost: 0, hasUnpriced: false },
  );
}

function CostTable({
  title,
  description,
  entries,
}: {
  title: string;
  description: string;
  entries: LlmCostEntry[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <h4 className="text-sm font-medium">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="overflow-hidden rounded-lg border">
        <div className="overflow-x-auto no-scrollbar">
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead className="text-right">Calls</TableHead>
                <TableHead className="text-right">Input tokens</TableHead>
                <TableHead className="text-right">Output tokens</TableHead>
                <TableHead className="text-right">Thinking tokens</TableHead>
                <TableHead className="text-right">Est. cost (USD)</TableHead>
                <TableHead className="text-right">Est. cost / call</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length ? (
                entries.map((entry) => (
                  <TableRow key={entry.model}>
                    <TableCell className="max-w-48 truncate font-medium">
                      {entry.model}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatTokens(entry.call_count)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatTokens(entry.total_input_tokens)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatTokens(entry.total_output_tokens)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatTokens(entry.total_thoughts_tokens)}
                    </TableCell>
                    <TableCell className="text-right">
                      <CostCell entry={entry} />
                    </TableCell>
                    <TableCell className="text-right">
                      <CostPerCallCell entry={entry} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    No usage recorded yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export function CostsPanel({ costs, isLoading, days }: CostsPanelProps) {
  const llmCosts = costs?.llm_costs ?? [];
  const moderationCosts = costs?.moderation_costs ?? [];

  const llmTotals = sumTotals(llmCosts);
  const moderationTotals = sumTotals(moderationCosts);
  const totalCost = llmTotals.cost + moderationTotals.cost;
  const totalCalls = llmTotals.calls + moderationTotals.calls;
  const hasUnpriced = llmTotals.hasUnpriced || moderationTotals.hasUnpriced;

  const summaryStats = [
    {
      title: "Est. Total Cost",
      value: hasUnpriced ? `${formatUsd(totalCost)}+` : formatUsd(totalCost),
      icon: "solar:dollar-line-duotone",
    },
    {
      title: "Total Calls",
      value: formatTokens(totalCalls),
      icon: "solar:programming-line-duotone",
    },
    {
      title: "LLM Calls",
      value: formatTokens(llmTotals.calls),
      icon: "solar:magic-stick-3-line-duotone",
    },
    {
      title: "Moderation Calls",
      value: formatTokens(moderationTotals.calls),
      icon: "solar:shield-check-line-duotone",
    },
  ];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 px-4 lg:px-6">
        <div className="grid grid-cols-2 gap-4 @xl/main:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 lg:px-6">
      <Card className="p-0">
        <CardContent className="flex w-full flex-col items-stretch px-0 sm:flex-row sm:flex-wrap">
          {summaryStats.map((item) => (
            <div
              key={item.title}
              className="min-w-0 flex-1 basis-1/2 border-b border-border last:border-b-0 sm:basis-1/4 sm:border-b-0 sm:border-e sm:last:border-e-0"
            >
              <div className="p-4 sm:p-6">
                <div className="flex flex-col gap-1">
                  <div className="flex items-start justify-between gap-2">
                    <h5 className="min-w-0 truncate text-sm font-medium text-muted-foreground">
                      {item.title}
                    </h5>
                    <div className="rounded-full p-2 text-primary outline outline-border">
                      <Icon icon={item.icon} width={16} height={16} />
                    </div>
                  </div>
                  <h5 className="text-2xl font-semibold">{item.value}</h5>
                  <p className="text-xs text-muted-foreground">
                    Last {days} days
                  </p>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {hasUnpriced && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="secondary">note</Badge>
          Some usage couldn't be priced — the total above is a floor, not the
          full cost.
        </div>
      )}

      <CostTable
        title="LLM usage"
        description="Gemini calls — currently event badge-image generation."
        entries={llmCosts}
      />

      <CostTable
        title="Moderation usage"
        description="Safety-moderation calls, priced via OpenRouter or jev depending on model."
        entries={moderationCosts}
      />

      <p className="text-xs text-muted-foreground">
        Aggregated from an audit log, not a live billing feed — this is
        reporting only, with no cost enforcement behind it. More infra cost
        categories (e.g. object storage) may appear here later.
      </p>
    </div>
  );
}
